/**
 * ToonMaterials — cel-shaded material factory with theme support.
 *
 * Carried over from v1 with two changes:
 *
 *   1. Node materials throughout. `MeshToonNodeMaterial` / `MeshBasicNodeMaterial`
 *      take the same constructor params as their legacy counterparts, and the
 *      legacy classes are the ones that throw `Uniform "null" not declared`
 *      under WebGPURenderer once you give them an emissive.
 *   2. The material key is a map SURFACE, not a v1 entity `type`. v2 arenas are
 *      `MapDef.platforms` with a `Surface`, so there is no `properties` bag to
 *      read and no per-entity color coming off the wire.
 *
 * Material caching still matters for the same reason it did in v1: a 200-platform
 * arena that builds 200 materials is 200 pipeline compiles on first frame.
 */

import * as THREE from 'three/webgpu';
import { uniform, mix, vec3, materialColor, materialEmissive } from 'three/tsl';
import { generateNormalMap, floorPanelTexture, conveyorTexture } from './ProceduralTextures.js';
import { registerConveyorMaterial } from './SurfaceShaders.js';

function createGradientTexture(steps) {
  const data = new Uint8Array(steps);
  for (let i = 0; i < steps; i++) {
    data[i] = Math.round((i / (steps - 1)) * 255);
  }
  const texture = new THREE.DataTexture(data, steps, 1, THREE.RedFormat);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.needsUpdate = true;
  return texture;
}

export const GRADIENT_2 = createGradientTexture(2);
export const GRADIENT_3 = createGradientTexture(3);
export const GRADIENT_4 = createGradientTexture(4);

// ─── Material themes (per-map look) ─────────────────────────────

const MATERIAL_THEMES = {
  stone: { emissiveMult: 0.1, surfaceEmissive: 0.3, colorShift: [0, 0, 0], normalScale: 0.6 },
  lava_rock: { emissiveMult: 0.3, surfaceEmissive: 0.4, colorShift: [0, 0.05, -0.05], normalScale: 0.8 },
  ice_crystal: { emissiveMult: 0.15, surfaceEmissive: 0.25, colorShift: [0, -0.1, 0.1], normalScale: 0.5 },
  neon: { emissiveMult: 0.5, surfaceEmissive: 0.6, colorShift: [0, 0.15, 0.05], normalScale: 0 },
  wood: { emissiveMult: 0.08, surfaceEmissive: 0.2, colorShift: [0, -0.05, -0.03], normalScale: 0.7 },
  candy: { emissiveMult: 0.25, surfaceEmissive: 0.35, colorShift: [0, 0.1, 0.08], normalScale: 0 },

  /*
   * RWA ROYALE — one identity per arena.
   *
   * The five above are the graybox inheritance: stone, wood, ice. They describe
   * MATERIALS, which is the right vocabulary for a party platformer and the
   * wrong one for a trading floor — nothing here is made of wood.
   *
   * `colorShift` is [hue, saturation, lightness] offset from the base surface
   * blue (#2f5e96, about 213 degrees). Hue is a fraction of the wheel, so a
   * move to green at 140 degrees is (140-213)/360 = -0.20. Each arena gets a
   * hue it owns, and no two are within 60 degrees of each other — the point of
   * theming five arenas is that a screenshot of one is not a screenshot of any
   * other.
   */
  /**
   * The Chart — a terminal: cold green light on a dark screen.
   *
   * Teal, not grass. -0.2 hue lands the deck on ~139 degrees, which is within a
   * few degrees of the mend pad's #4ade80 — the pool that heals you disappeared
   * into the floor of the arena that hosts it. A theme must never take the deck
   * to the hue of a surface that teaches a rule; see `surfaceColor`.
   */
  // Lightness goes UP, not down. The deck is now authored dark (see
  // createDeckMaterial), and in the reference the candles are the BRIGHT thing
  // standing on a dark floor — the value contrast is what makes them read as
  // objects rather than as pattern. Shifting both the same way was why the
  // first pass looked flat.
  terminal: { emissiveMult: 0.42, surfaceEmissive: 0.55, colorShift: [-0.115, 0.22, 0.2], normalScale: 0.15 },
  /** The Pit — brass and amber. Warm, worn, the only analogue room here. */
  pit: { emissiveMult: 0.16, surfaceEmissive: 0.3, colorShift: [0.51, 0.18, 0.02], normalScale: 0.75 },
  /** The Exchange — institutional steel. Desaturated on purpose: it is the one
   *  arena whose drama is architecture rather than colour. */
  exchange: { emissiveMult: 0.12, surfaceEmissive: 0.22, colorShift: [-0.03, -0.28, 0.06], normalScale: 0.55 },
  /** Bubble — iridescent violet, pale and about to pop. */
  bubble: { emissiveMult: 0.3, surfaceEmissive: 0.42, colorShift: [0.2, 0.05, 0.14], normalScale: 0.2 },
  /** HFT Floor — server-room magenta. Hot, synthetic, no texture at all. */
  hft: { emissiveMult: 0.5, surfaceEmissive: 0.6, colorShift: [0.3, 0.24, 0.0], normalScale: 0 },
};

let currentMaterialTheme = null;

export function setMaterialTheme(themeName) {
  currentMaterialTheme = themeName && MATERIAL_THEMES[themeName] ? themeName : null;
}

export function getMaterialTheme() {
  return currentMaterialTheme;
}

/**
 * The current theme's [hue, sat, lightness] offset, or null for the graybox.
 *
 * The deck shader lives in SurfaceShaders.js, which this module imports — so it
 * cannot import back to ask what the theme is. ArenaRenderer already holds
 * both and passes this down, which keeps the cycle from existing at all.
 */
export function materialThemeColorShift() {
  return theme()?.colorShift ?? null;
}

function theme() {
  return currentMaterialTheme ? MATERIAL_THEMES[currentMaterialTheme] : null;
}

// ─── Surface palette ────────────────────────────────────────────

/** One base color per `Surface.kind` from `@chaos/sim`'s MapDef. */
const SURFACE_COLORS = {
  normal: 0x2f5e96,
  ice: 0xb3e5fc,
  conveyor: 0xe67e22,
  bounce: 0x2ecc71,
  speed: 0xf1c40f,
  // Hazard and mend borrow the HUD's damage red and health green rather than
  // inventing arena colours. A pool that hurts you and the health bar it drains
  // should be the same red — the player learns one association, not two.
  hazard: 0xff3d3d,
  heal: 0x4ade80,
};

/**
 * The colour of a surface kind under the current theme.
 *
 * The theme shift lives HERE and nowhere else. It used to live in
 * `buildSurfaceMaterial`, which meant the instanced path — `isInstanceable` is
 * true for every solid non-ice non-conveyor platform, so nearly all of them —
 * read the raw base colour and ignored the theme entirely. Five arenas with
 * five themes rendered as the same blue-grey box; only the sky changed.
 *
 * ONLY `normal` is themed. The other kinds each teach the player a rule —
 * hazard burns, heal mends, bounce launches — and a rule you have to relearn
 * per arena is not a rule. Concretely: The Pit's +0.51 hue shift would swing
 * the hazard red round to green, so the pool that kills you would wear the
 * colour of the pool that heals you.
 *
 * The matching constraint on the other side: a THEME must not take `normal` to
 * a signal's hue either, or the signal vanishes into the deck instead of the
 * deck stealing the signal's meaning. `terminal` shipped broken exactly that
 * way for one build. Base `normal` is ~211 degrees; keep the shifted result
 * clear of hazard (~0) and heal (~142).
 */
export function surfaceColor(kind) {
  const color = new THREE.Color(SURFACE_COLORS[kind] ?? SURFACE_COLORS.normal);
  const t = theme();
  if (kind === 'normal' && t?.colorShift) {
    const [dh, ds, dl] = t.colorShift;
    color.offsetHSL(dh, ds, dl);
  }
  return color;
}

const _materialCache = new Map();

/**
 * World size of one texture tile, in units. The texture draws 2x2 access-floor
 * panels, so a panel lands at ~1.1 units — about a pace, which is what a real
 * raised floor panel is and what makes a platform legible as a SURFACE rather
 * than a coloured slab.
 */
const PANEL_TILE_WORLD = 2.2;

/**
 * How many times to repeat the panel texture across a platform of this size.
 *
 * A box's UVs run 0..1 across each face whatever the face measures, so one
 * fixed repeat gives a 3-unit candle crisp panels and a 9-unit island panels
 * four and a half units across — which is why the islands read as flat slabs
 * next to hand-built props. Quantised to whole numbers so that platforms of
 * similar size share a material instead of each one compiling its own.
 */
export function panelRepeatFor(sizeUnits) {
  return Math.max(1, Math.min(8, Math.round(sizeUnits / PANEL_TILE_WORLD)));
}

/**
 * A material for one map surface.
 *
 * Cached by kind + theme, because every platform of a kind looks identical —
 * v2 has no per-entity colour on the wire. Conveyors are the exception: their
 * texture scrolls along a per-platform direction and speed, so sharing one
 * material would make half of them scroll backwards.
 *
 * @param {{kind:string, dirX?:number, dirZ?:number, speed?:number}} surface
 */
export function createSurfaceMaterial(surface, panelRepeat = 1) {
  const kind = surface.kind;
  if (kind === 'conveyor') {
    const mat = buildSurfaceMaterial(surface);
    registerConveyorMaterial(mat, surface.speed ?? 6, [surface.dirX ?? 1, 0, surface.dirZ ?? 0]);
    return mat;
  }

  const key = `${kind}|${currentMaterialTheme || ''}|${panelRepeat}`;
  const cached = _materialCache.get(key);
  if (cached) return cached;

  const mat = buildSurfaceMaterial(surface, panelRepeat);
  _materialCache.set(key, mat);
  return mat;
}

function buildSurfaceMaterial(surface, panelRepeat = 1) {
  const kind = surface.kind;
  const t = theme();

  if (kind === 'ice') {
    // Ice keeps a physical material: the clearcoat is what sells "you will slide".
    return new THREE.MeshPhysicalNodeMaterial({
      color: '#b3e5fc',
      roughness: 0.05,
      metalness: 0.1,
      clearcoat: 1.0,
      clearcoatRoughness: 0.1,
      transparent: true,
      opacity: 0.85,
      emissive: '#b3e5fc',
      emissiveIntensity: 0.1,
    });
  }

  const color = surfaceColor(kind);

  // Emissive at v1's 0.32 was a uniform wash that flattened the toon ramp —
  // half the shading the key light produces was being added back everywhere.
  const material = new THREE.MeshToonNodeMaterial({
    color,
    gradientMap: GRADIENT_3,
    emissive: color,
    emissiveIntensity: t ? t.surfaceEmissive * 0.5 : 0.14,
  });

  if (kind === 'conveyor') {
    // A fresh texture per conveyor: `updateConveyorScrolls` animates
    // `material.map.offset`, and a shared texture would scroll every belt on the
    // map in whichever direction was registered last.
    material.map = conveyorTexture('#e67e22').clone();
    material.map.needsUpdate = true;
    material.normalMap = generateNormalMap('metal');
    material.normalScale = new THREE.Vector2(0.5, 0.5);
    return material;
  }

  const dark = color.clone().multiplyScalar(0.85);
  material.map = floorPanelTexture(`#${color.getHexString()}`, `#${dark.getHexString()}`, panelRepeat);

  const normalScale = t ? t.normalScale : 0.5;
  if (normalScale > 0) {
    material.normalMap = generateNormalMap('metal');
    material.normalScale = new THREE.Vector2(normalScale, normalScale);
  }
  return material;
}

export function clearMaterialCache() {
  for (const mat of _materialCache.values()) mat.dispose();
  _materialCache.clear();
}

/**
 * Shared material for the instanced platform batches. Colors ride per-instance.
 *
 * On a graybox map every solid non-ice/non-conveyor platform — including the
 * 90x90 ground slab — goes through this material, so it carries the same brick
 * color map as the non-instanced path or the arena has no surface detail at
 * all. The map is a near-white neutral so the per-instance tint stays true.
 * Emissive is deliberately faint: a flat grey emissive at 0.32 lifted the whole
 * arena toward pale grey-blue (it cannot ride the instance color, so any
 * strength it has reads as uniform wash, not glow).
 */
/**
 * The shared material for one batch of instanced platforms.
 *
 * `color` stays white and the texture stays neutral grey because the tint
 * arrives per instance via `setColorAt` — tinting here as well would square the
 * colour. The emissive cannot be per-instance, but every platform in a batch
 * shares a surface kind, so one themed glow per batch is exactly right.
 *
 * Emissive matches `buildSurfaceMaterial`'s formula on purpose: whether a
 * platform is instanced is a draw-call optimisation, and an optimisation that
 * changes how a surface looks is a bug wearing a performance costume.
 *
 * @param {string} kind - surface kind, for the themed glow colour
 */
export function createInstancedPlatformMaterial(kind = 'normal', panelRepeat = 1) {
  const t = theme();
  const material = new THREE.MeshToonNodeMaterial({
    color: 0xffffff,
    gradientMap: GRADIENT_3,
    emissive: surfaceColor(kind),
    emissiveIntensity: t ? t.surfaceEmissive * 0.5 : 0.14,
  });
  material.map = floorPanelTexture('#e8e8e8', '#c4c4c4', panelRepeat);

  const normalScale = t ? t.normalScale : 0.5;
  if (normalScale > 0) {
    material.normalMap = generateNormalMap('metal');
    material.normalScale = new THREE.Vector2(normalScale, normalScale);
  }
  return material;
}

// ─── Hit flash ──────────────────────────────────────────────────

/**
 * A per-material flash uniform, mixed in via TSL nodes.
 *
 * This is the WebGPU-safe substrate for hit feedback: poking `.emissive` on a
 * node material after a `colorNode` is assigned is ignored or breaks, so the
 * flash rides the node graph from creation. At 0 the material renders exactly
 * as it did before this existed (`materialColor`/`materialEmissive` are the
 * same nodes the material would build for itself).
 */
function attachFlash(material, { lit } = { lit: true }) {
  const flash = uniform(0);
  material.colorNode = mix(materialColor, vec3(1, 1, 1), flash);
  if (lit) {
    // The emissive push is what blows the body past its toon shading to a flat
    // white under ACES instead of a merely pale character.
    material.emissiveNode = mix(materialEmissive, vec3(1.6, 1.6, 1.6), flash);
  }
  // Not in `userData`: Material.copy() JSON-round-trips userData, and a live
  // TSL node in there would throw on any future material.clone()/toJSON().
  material._flashUniform = flash;
  return material;
}

/**
 * Pulse a character (or any subtree built from this factory) toward white.
 * `t` is 0..1; 0 restores the original look. Materials without a flash uniform
 * (outline hulls, arena surfaces) are skipped.
 */
export function setFlashAmount(object3D, t) {
  // Almost every call is zero on an already-zero body — the character mockup
  // alone asks ten bodies per frame. Traversing a ~50-node hierarchy to write
  // the value it already holds is the most-called no-op in the client.
  if (t === 0 && object3D.userData.flashAmount === 0) return;
  if (!object3D || typeof object3D.traverse !== 'function') return;
  const v = Math.max(0, Math.min(1, t ?? 0));
  // Bridge for pooled characters: a batched body has no meshes to traverse,
  // so the CharacterBatcher reads this back and mixes the flash into the
  // per-instance colour instead. Harmless on everything else.
  object3D.userData.flashAmount = v;
  object3D.traverse((child) => {
    if (!child.isMesh) return;
    const mats = Array.isArray(child.material) ? child.material : [child.material];
    for (const mat of mats) {
      const u = mat?._flashUniform;
      if (u) u.value = v;
    }
  });
}

/**
 * Character material.
 *
 * `color` is CHARACTERS[id].colorHex and nothing else. A per-instance random hue
 * is the v1 bug this whole file exists downstream of.
 */
export function createPlayerToonMaterial(color, { emissiveIntensity = 0.32 } = {}) {
  return attachFlash(
    new THREE.MeshToonNodeMaterial({
      color,
      // Two steps, not three: characters are the one thing that should read as
      // hard-cel against the softer arena, and the mid step just blurred the
      // terminator under the raised key.
      gradientMap: GRADIENT_2,
      emissive: color,
      emissiveIntensity,
    })
  );
}

/** Unlit accent — eyes, trim, glyphs. Node variant, never MeshBasicMaterial. */
export function createAccentMaterial(color, opts = {}) {
  // Unlit: colorNode alone reaches full white, and basic materials have no
  // emissive slot to push.
  return attachFlash(new THREE.MeshBasicNodeMaterial({ color, ...opts }), { lit: false });
}

export function createGroundToonMaterial() {
  const mat = new THREE.MeshToonNodeMaterial({
    color: 0x2d3436,
    gradientMap: GRADIENT_4,
    emissive: 0x2d3436,
    emissiveIntensity: 0.15,
  });
  mat.normalMap = generateNormalMap('stone');
  mat.normalScale = new THREE.Vector2(0.3, 0.3);
  return mat;
}
